import { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    CircularProgress,
    Alert,
    AlertTitle,
    InputAdornment,
    IconButton,
    FormControlLabel,
    Switch,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { validateLoginPageURL } from "../../utils/validateForm";
import ProjectService from "../../services/ProjectService";
import { useProject } from "../../context/ProjectContext";
import { useNotification } from "../../context/NotificationContext";
import { isExcelhireUrl } from "../../utils/excelhireAuth";

const UpdateLoginCredentialsModal = ({
    updateCredentialsOpen,
    setUpdateCredentialsOpen,
    isOTPSettingActiveForUpdate,
    setIsOTPSettingActiveForUpdate,
    onRestartComplete,
    onClose,
    isInitialSetup = false,
}) => {
    const { selectedProject } = useProject();
    const showNotification = useNotification();

    const [updateCredentialsLoading, setUpdateCredentialsLoading] =
        useState(false);
    const [loginPageUrl, setLoginPageUrl] = useState("");
    const [loginPageURLError, setLoginPageURLError] = useState("");
    const [login, setLogin] = useState("");
    const [organisation, setOrganisation] = useState("");
    const [password, setPassword] = useState("");
    const [isOTPBased, setIsOTPBased] = useState(false);
    const [loginError, setLoginError] = useState("");
    const [organisationError, setOrganisationError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const isExcelhireProject =
        isExcelhireUrl(selectedProject?.url) || isExcelhireUrl(loginPageUrl);

    // Load initial data when modal opens
    useEffect(() => {
        if (updateCredentialsOpen && selectedProject?.id) {
            const loadProjectData = async () => {
                try {
                    // Only load existing data if it's not initial setup
                    if (!isInitialSetup) {
                        const projectLoginData =
                            await ProjectService.getProjectLoginDataById(
                                selectedProject.id
                            );
                        setLoginPageUrl(projectLoginData.login_page_url || "");
                        setLogin(projectLoginData.login || "");
                        setOrganisation(projectLoginData.organisation || "");
                        setIsOTPBased(!!projectLoginData.one_time_password);
                    } else {
                        // For initial setup, start with empty fields
                        setLoginPageUrl("");
                        setLogin("");
                        setOrganisation("");
                        setIsOTPBased(false);
                    }
                } catch (error) {
                    console.error("Error loading project login data:", error);
                    if (!isInitialSetup) {
                        showNotification(
                            "Failed to load login data",
                            "error",
                            ""
                        );
                    }
                }
            };

            loadProjectData();
        }
    }, [
        updateCredentialsOpen,
        selectedProject?.id,
        showNotification,
        isInitialSetup,
    ]);

    const handleClose = () => {
        setUpdateCredentialsOpen(false);
        setIsOTPSettingActiveForUpdate(false);
        setLoginPageUrl("");
        setLoginPageURLError("");
        setLogin("");
        setOrganisation("");
        setPassword("");
        setLoginError("");
        setOrganisationError("");
        setShowPassword(false);
        if (onClose) {
            onClose();
        }
    };

    const handleLoginPageURLChange = (e) => {
        setLoginPageUrl(e.target.value.trim());
        if (loginPageURLError) {
            setLoginPageURLError("");
        }
    };

    const handleLoginChange = (e) => {
        setLogin(e.target.value.trim());
        if (loginError) {
            setLoginError("");
        }
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value.trim());
    };

    const handleOrganisationChange = (e) => {
        setOrganisation(e.target.value);
        if (organisationError) {
            setOrganisationError("");
        }
    };

    const handleClickShowPassword = () => {
        setShowPassword((prev) => !prev);
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handleUpdateCredentials = async () => {
        if (!validateLoginPageURL(loginPageUrl)) {
            setLoginPageURLError("Invalid login page URL.");
            return;
        }

        if (!login.trim()) {
            setLoginError("Login is required.");
            return;
        }

        if (isExcelhireProject && !organisation.trim()) {
            setOrganisationError("Organisation is required.");
            return;
        }

        try {
            setUpdateCredentialsLoading(true);

            if (isInitialSetup) {
                // For initial setup, use the rescan endpoint to set up login
                await ProjectService.rescanProjectLoginData(
                    selectedProject.id,
                    {
                        loginPageUrl,
                        loginFormId: "", // Empty for initial setup
                        login,
                        password: password.trim() || undefined,
                        organisation:
                            isExcelhireProject && organisation.trim()
                                ? organisation.trim()
                                : undefined,
                        isOTPBased,
                    }
                );
            } else {
                // For updates, use the update endpoint
                const updateData = {
                    login_page_url: loginPageUrl,
                    login: login,
                };

                if (isExcelhireProject) {
                    updateData.organisation = organisation.trim();
                }

                if (password.trim()) {
                    updateData.password = password;
                }

                if (isOTPSettingActiveForUpdate) {
                    updateData.isOTPBased = isOTPBased;
                }

                await ProjectService.updateLoginCredentials(
                    selectedProject.id,
                    updateData
                );
            }

            // If this is a restart operation, notify parent to reset login steps
            if (onRestartComplete) {
                onRestartComplete();
            }

            showNotification(
                isInitialSetup
                    ? "Login setup initiated successfully"
                    : "Login credentials updated successfully",
                "success",
                ""
            );
            handleClose();
        } catch (error) {
            console.error("Error updating login credentials:", error);
            showNotification(
                error.response?.data?.message ||
                    "Failed to update login credentials",
                "error",
                ""
            );
        } finally {
            setUpdateCredentialsLoading(false);
        }
    };

    return (
        <Dialog open={updateCredentialsOpen} onClose={handleClose}>
            <Box className="updateLoginCredentialsModal">
                <DialogTitle className="updateLoginCredentialsModalTitle">
                    {isInitialSetup
                        ? "Setup Login Credentials"
                        : "Login Credentials"}
                </DialogTitle>
                <DialogContent className="updateLoginCredentialsModalContent">
                    {(isInitialSetup) && (
                        <Alert
                            className="updateLoginCredentialsModalAlert"
                            severity="info"
                        >
                            <AlertTitle className="updateLoginCredentialsModalAlertTitle">
                                Important
                            </AlertTitle>
                            <span>
                                The login form must have its own{" "}
                                <strong>unique URL</strong> for the scanning
                                process to work and{" "}
                                <strong>without Captcha</strong>
                            </span>
                        </Alert>
                    )}
                        <TextField
                            sx={{ marginTop: '16px' }}
                            label="Login page URL"
                            type="url"
                            fullWidth
                            variant="outlined"
                            value={loginPageUrl}
                            onChange={handleLoginPageURLChange}
                            required
                            error={!!loginPageURLError}
                            helperText={loginPageURLError + " "}
                            // margin="normal"
                        />
                    <TextField
                        label="Login"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={login}
                        onChange={handleLoginChange}
                        required
                        autoComplete="new-login"
                        error={!!loginError}
                        helperText={loginError + " "}
                        margin={
                            isInitialSetup
                                ? "none"
                                : "normal"
                        }
                    />
                    {isExcelhireProject ? (
                        <TextField
                            label="Organisation"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={organisation}
                            onChange={handleOrganisationChange}
                            required
                            error={!!organisationError}
                            helperText={organisationError + " "}
                            margin="normal"
                        />
                    ) : null}
                    <TextField
                        label="Password (optional)"
                        type={showPassword ? "text" : "password"}
                        fullWidth
                        variant="outlined"
                        value={password}
                        onChange={handlePasswordChange}
                        autoComplete="new-password"
                        // margin="normal"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleClickShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                        sx={{ mr: -0.5 }}
                                    >
                                        {showPassword ? (
                                            <VisibilityOff />
                                        ) : (
                                            <Visibility />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 3 }}
                    />
                        <Box className="updateLoginCredentialsModalOTPContainer">
                            <FormControlLabel
                                className="updateLoginCredentialsModalOTPSwitch"
                                control={
                                    <Switch
                                        checked={isOTPBased}
                                        onChange={(e) =>
                                            setIsOTPBased(e.target.checked)
                                        }
                                    />
                                }
                                label=""
                            />
                            <Typography
                                variant="body1"
                                sx={{ mt: "2px", mr: "6px" }}
                            >
                                Is it OTP?
                            </Typography>
                        </Box>
                </DialogContent>
                <DialogActions className="updateLoginCredentialsModalActions">
                    <Button
                        className="updateLoginCredentialsModalButton"
                        onClick={handleClose}
                        color="secondary"
                        variant="outlined"
                    >
                        CANCEL
                    </Button>
                    {updateCredentialsLoading ? (
                        <LoadingButton
                            loading
                            className="updateLoginCredentialsModalButton"
                            variant="contained"
                            loadingIndicator={
                                <CircularProgress color="white" size={16} />
                            }
                        />
                    ) : (
                        <Button
                            className="updateLoginCredentialsModalButton"
                            onClick={handleUpdateCredentials}
                            color="secondary"
                            variant="contained"
                        >
                            {isInitialSetup ? "SETUP" : "UPDATE"}
                        </Button>
                    )}
                </DialogActions>
            </Box>
        </Dialog>
    );
};

export default UpdateLoginCredentialsModal;
